import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';

import { EmptyState } from '@repo/ui';
import { PageLayout } from '@xtnd/web/shared/layout';
import { useReportFeatureEnabled } from '../../hooks';
import { getReportCards } from '../../config/reportDefinitions';
import type { ReportCard } from '../../types';
import { ReportCardComponent, ReportsPageSkeleton } from './components';

export default function ReportsPage() {
  const navigate = useNavigate();

  const {
    isLoading,
    isReportingEnabled,
    isArExceptionReportingEnabled,
    isTrustReconciliationEnabled,
    isMatterLedgerReportingEnabled,
    isMatterStatementReportingEnabled,
  } = useReportFeatureEnabled();

  // Get report cards with enabled flags
  const reportCards = useMemo(
    () =>
      getReportCards(
        isReportingEnabled,
        isArExceptionReportingEnabled,
        isTrustReconciliationEnabled,
        isMatterLedgerReportingEnabled,
        isMatterStatementReportingEnabled
      ),
    [
      isReportingEnabled,
      isArExceptionReportingEnabled,
      isTrustReconciliationEnabled,
      isMatterLedgerReportingEnabled,
      isMatterStatementReportingEnabled,
    ]
  );

  // Filter enabled reports
  const availableReports = useMemo(
    () => reportCards.filter(report => report.isEnabled),
    [reportCards]
  );

  // Group reports by category
  const groupedReports = useMemo(
    () =>
      availableReports.reduce(
        (groups, report) => {
          const category = report.category;
          if (!groups[category]) {
            groups[category] = [];
          }
          groups[category].push(report);
          return groups;
        },
        {} as Record<string, ReportCard[]>
      ),
    [availableReports]
  );

  const handleReportClick = (report: ReportCard) => {
    if (report.isComingSoon) {
      // For coming soon reports, just show a toast or do nothing
      return;
    }

    if (report.route) {
      navigate(report.route);
    }
  };

  return (
    <PageLayout
      title="Reports"
      description="Access and analyze your business data with comprehensive reporting tools."
      icon="HiChartBar"
      isLoading={isLoading}
      customSkeleton={<ReportsPageSkeleton />}
      isDisabled={!isReportingEnabled}
      disabledState={{
        title: 'Reporting Module Disabled',
        description:
          'The reporting module is not enabled for this tenant. Please contact your administrator to enable reporting features.',
        helpText: 'You can enable the reporting module in Settings → Reporting Configuration',
      }}
    >
      {availableReports.length === 0 ? (
        <EmptyState
          icon="HiTable"
          title="No Reports Available"
          description="No reporting features are currently enabled for this tenant. Please configure reporting modules in Settings."
          helpText={
            <>
              Configure reporting features in <strong>Settings → Reporting Configuration</strong>
            </>
          }
          variant="outlined"
          padding="lg"
          titleSize="md"
        />
      ) : (
        <div className="space-y-8">
        {Object.entries(groupedReports).map(([category, reports]) => (
          <div key={category}>
            {/* Category Header */}
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white mb-1">
                {category}
              </h2>
              <div className="h-px bg-xtnd-dark-100 dark:bg-xtnd-dark-600"></div>
            </div>

            {/* Report Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reports.map(report => (
                <ReportCardComponent
                  key={report.id}
                  report={report}
                  onReportClick={handleReportClick}
                />
              ))}
            </div>
          </div>
        ))}
        </div>
      )}
    </PageLayout>
  );
}
